import Vue from "vue";
import config from '@/constants/config'
import pools, { isFactoryPoolDataLoaded } from '@/constants/pools'
import * as BN from 'bignumber.js'
import { contractAbis, susd, ERC20_abi, synthetixExchanger_address, synthetixExchanger_ABI, multicall_abi, multicall_address, CHI_address, liquiditygauge_abi, liquiditygauge_abi_v2, liquiditygauge_v3, liquiditygauge_v4, minter_ABI, minter_address, registry_abi, registry_address, migratorABI, isFactoryAbiDataLoaded } from '@/constants/abis'
import web3Init from './init'
import * as common from './utils/common.js'
import { arrayToHashmap } from './utils/helpers'
import { sleepUntil } from './utils/Async'

var N_COINS = pools.length > 0 ? pools[0].coins.length : null;
var coin_precisions = [1e18, 1e6, 1e6]; // should be only [1e18, 1e6] for fantom/eth but non breaking
var old_swap_address = '0x2e60CF74d81ac34eB21eEff58Db4D385920ef419';
var old_token_address = '0x3740fb63ab7a09891d7c0d4299442A551D06F5fD';
var swap_address = pools.length > 0 ? pools[0].addresses.swap : null; // Default to first pool info
var token_address = pools.length > 0 ? pools[0].addresses.lpToken : null; // Default to first pool info

export const LENDING_PRECISION = 1e18;
export const PRECISION = 1e18;

var migration_address = '0x54Ee22d5593FC76fB20EafAb66C45aAb3268B800';
export const infura_url = config.rpcUrl;

// Note: the only diff is that the coins hashmap uses e.g. "cdai" rather than "dai" for "cDAI"
const currencies = arrayToHashmap(pools.map(({ id, coins }) => [
  id,
  arrayToHashmap(coins.map(({ id, symbol }) => [id, symbol])),
]));

const lending_currencies = arrayToHashmap(
  pools.filter(({ isLendingPool }) => isLendingPool)
    .map(({ id, underlyingCoins }) => [
      id,
      arrayToHashmap(underlyingCoins.map(({ id, symbol }) => [id, symbol])),
    ])
);

const meta_currencies = arrayToHashmap(
  pools.filter(({ isMetaPool }) => isMetaPool)
    .map(({ id, coins, metaCoins }) => [
      id,
      //arrayToHashmap(coins.slice(1).concat(metaCoins).map(({ id, symbol }) => [id, symbol])),  <==== this is what we need for crypto pools.
      arrayToHashmap(coins.slice(0, -1).concat(metaCoins).map(({ id, symbol }) => [id, symbol])),
    ])
);


const all_currencies = arrayToHashmap(
  pools.filter(({ isMetaPool }) => isMetaPool)
    .map(({ id, coins, metaCoins }) => [
      id,
      arrayToHashmap(coins.concat(metaCoins).map(({ id, symbol }) => [id, symbol])),
    ])
);

export const allCurrencies = currencies

export const allMetaCurrencies = { ...currencies, ...all_currencies }

export const poolMenu = arrayToHashmap(pools.map(({ id, name }) => [id, name]));

export const gas = {
  swap: new Proxy({
    compound: {
      exchange: () => 600000,
      exchange_underlying: () => 1200000
    },
    usdt: {
      exchange: () => 600000,
      exchange_underlying: () => 1200000
    },
    iearn: {
      exchange: () => 800000,
      exchange_underlying: () => 1600000
    },
    busd: {
      exchange: () => 800000,
      exchange_underlying: () => 1600000
    },
    susd: {
      exchange: () => 600000,
      exchange_underlying: () => 1200000,
    },
    susdv2: {
      exchange: (i, j) => (i == 3 || j == 3) ? 1000000 : 300000,
      exchange_underlying: (i, j) => (i == 3 || j == 3) ? 1000000 : 300000,
    },
    pax: {
      exchange: () => 800000,
      exchange_underlying: () => 1600000
    },
    ren: {
      exchange: () => 300000,
      exchange_underlying: () => 300000,
    },
    sbtc: {
      exchange: (i, j) => (i == 2 || j == 2) ? 1000000 : 300000,
      exchange_underlying: (i, j) => (i == 2 || j == 2) ? 1000000 : 300000,
    },
    sbtc2: {
      exchange: (i, j) => (i == 2 || j == 2) ? 1000000 : 300000,
      exchange_underlying: (i, j) => (i == 2 || j == 2) ? 1000000 : 300000,
    },
    hbtc: {
      exchange: () => 300000,
      exchange_underlying: () => 300000,
    },
    '3pool': {
      exchange: () => 300000,
      exchange_underlying: () => 300000,
    },
    '2pool': {
      exchange: () => 300000,
      exchange_underlying: () => 300000,
    },
    '4pool': {
      exchange: () => 300000,
      exchange_underlying: () => 300000,
    },
    'fraxusdc': {
      exchange: () => 300000,
      exchange_underlying: () => 300000,
    },
    gusd: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    husd: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    usdk: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    usdn: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    linkusd: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    musd: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    rsv: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    tbtc: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    dusd: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    pbtc: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    bbtc: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    obtc: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },

    ust: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    eurs: {
      exchange: () => 1000000,
      exchange_underlying: () => 1000000,
    },
    eurt: {
      exchange: () => 1000000,
      exchange_underlying: () => 1000000,
    },
    seth: {
      exchange: () => 600000,
      exchange_underlying: () => 600000,
    },
    aave: {
      exchange: () => 800000,
      exchange_underlying: () => 1600000
    },
    geist: {
      exchange: () => 800000,
      exchange_underlying: () => 1600000
    },
    idle: {
      exchange: () => 800000,
      exchange_underlying: () => 1600000
    },
    steth: {
      exchange: () => 300000,
      exchange_underlying: () => 300000,
    },
    saave: {
      exchange: () => 800000,
      exchange_underlying: () => 1600000
    },
    ankreth: {
      exchange: () => 300000,
      exchange_underlying: () => 300000,
    },
    usdp: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    ib: {
      exchange: () => 600000,
      exchange_underlying: () => 1200000
    },
    link: {
      exchange: () => 300000,
      exchange_underlying: () => 300000,
    },
    '2pool': {
      exchange: () => 300000,
      exchange_underlying: () => 300000,
    },
    fusdt: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    tusd: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    frax: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    lusd: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    factory: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    busdv2: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    reth: {
      exchange: () => 300000,
      exchange_underlying: () => 300000,
    },
    alusd: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    rai: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    eureusd: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    tricrypto: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    tricrypto2: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    atricrypto: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    atricrypto2: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    eursusd: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    crveth: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    cvxeth: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    teth: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    spelleth: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    eurtusd: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    euroc: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    frxeth: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    xautusd: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    atricrypto3: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    aaveV3: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
    mim: {
      exchange: () => 300000 * 2,
      exchange_underlying: () => 300000 * 2,
    },
  }, {
    get: function(target, name) {
      if (typeof name === 'string' && name.startsWith('factory-')) return target.factory;

      if (
        typeof name === 'string' &&
        !(name in target) &&
        name !== 'inspect' &&
        name !== 'state' &&
        name !== 'render' &&
        !name.startsWith('_') // Ignore "private" props
      ) {
        throw new Error(`Property "${name}" not found on object "gas.swap", the pool is probably missing a config object there`);
      }

      return target[name];
    },
  }),
  deposit: new Proxy({
    compound: 1300000,
    usdt: 1300000,
    iearn: 1300000,
    busd: 1300000,
    susd: 1300000,
    susdv2: 600000,
    pax: 1300000,
    ren: 300000,
    sbtc: 600000,
    sbtc2: 600000,
    hbtc: 300000,
    '3pool': 300000,
    '2pool': 300000,
    '4pool': 300000,
    'fraxusdc': 300000,
    gusd: 300000 * 2,
    husd: 300000 * 2,
    usdk: 300000 * 2,
    usdn: 300000 * 2,
    linkusd: 300000 * 2,
    musd: 300000 * 2,
    rsv: 300000 * 2,
    tbtc: 300000 * 2,
    dusd: 300000 * 2,
    pbtc: 300000 * 2,
    bbtc: 300000 * 2,
    obtc: 300000 * 2,
    ust: 300000 * 2,
    eurs: 600000,
    eurt: 600000,
    seth: 600000,
    aave: 1300000,
    geist: 4000000,
    idle: 2000000,
    steth: 250000,
    saave: 1300000,
    ankreth: 250000,
    usdp: 300000 * 2,
    ib: 1300000,
    link: 300000,
    '2pool': 300000,
    fusdt: 300000 * 2,
    tusd: 300000 * 2,
    frax: 300000 * 2,
    lusd: 300000 * 2,
    factory: 300000 * 2,
    busdv2: 300000 * 2,
    reth: 250000,
    alusd: 300000 * 2,
    rai: 300000 * 2,
    eureusd: 300000 * 2,
    tricrypto: 300000 * 2,
    tricrypto2: 300000 * 2,
    mim: 300000 * 2,
    eurtusd: 300000,
    euroc: 300000,
    frxeth: 300000,
    xautusd: 300000,
    eursusd: 300000,
    crveth: 300000,
    cvxeth: 300000,
    teth: 300000,
    spelleth: 300000,

    atricrypto: 2500000,
    atricrypto2: 2500000,
    atricrypto3: 2500000,
    aaveV3: 2500000,
  }, {
    get: function(target, name) {
      if (typeof name === 'string' && name.startsWith('factory-')) return target.factory;

      if (
        typeof name === 'string' &&
        !(name in target) &&
        name !== 'inspect' &&
        name !== 'state' &&
        name !== 'render' &&
        !name.startsWith('_') // Ignore "private" props
      ) {
        throw new Error(`Property "${name}" not found on object "gas.deposit", the pool is probably missing a config object there`);
      }

      return target[name];
    },
  }),
  withdraw: new Proxy({
    compound: {
      imbalance: x => 1500000,
    },
    usdt: {
      imbalance: x => 1500000,
    },
    iearn: {
      imbalance: x => (12642 * x + 474068) * 2.5 | 0,
    },
    busd: {
      imbalance: x => (12642 * x + 474068) * 2.5 | 0,
    },
    susd: {
      imbalance: x => 1000000,
    },
    susdv2: {
      imbalance: x => 600000,
    },
    pax: {
      imbalance: x => (12642 * x + 474068) * 2.5 | 0,
    },
    ren: {
      imbalance: x => 600000,
    },
    sbtc: {
      imbalance: x => 800000,
    },
    sbtc2: {
      imbalance: x => 800000,
    },
    hbtc: {
      imbalance: x => 600000,
    },
    '3pool': {
      imbalance: x => 600000,
    },
    '4pool': {
      imbalance: x => 600000,
    },
    'fraxusdc': {
      imbalance: x => 600000,
    },
    '2pool': {
      imbalance: x => 600000,
    },
    gusd: {
      imbalance: x => 600000 * 2,
    },
    husd: {
      imbalance: x => 600000 * 2,
    },
    usdk: {
      imbalance: x => 600000 * 2,
    },
    usdn: {
      imbalance: x => 600000 * 2,
    },
    linkusd: {
      imbalance: x => 600000 * 2,
    },
    musd: {
      imbalance: x => 600000 * 2,
    },
    rsv: {
      imbalance: x => 600000 * 2,
    },
    tbtc: {
      imbalance: x => 600000 * 2,
    },
    dusd: {
      imbalance: x => 600000 * 2,
    },
    pbtc: {
      imbalance: x => 600000 * 2,
    },
    bbtc: {
      imbalance: x => 600000 * 2,
    },
    obtc: {
      imbalance: x => 600000 * 2,
    },
    ust: {
      imbalance: x => 600000 * 2,
    },
    eurs: {
      imbalance: x => 600000,
    },
    eurt: {
      imbalance: x => 600000,
    },
    seth: {
      imbalance: x => 600000,
    },
    aave: {
      imbalance: x => (12642 * x + 474068) * 2.5 | 0,
    },
    geist: {
      imbalance: x => (12642 * x + 474068) * 2.5 | 0,
    },
    idle: {
      imbalance: x => (12642 * x + 474068) * 2.5 | 0,
    },
    steth: {
      imbalance: x => 600000,
    },
    saave: {
      imbalance: x => (12642 * x + 474068) * 2.5 | 0,
    },
    ankreth: {
      imbalance: x => 600000,
    },

    usdp: {
      imbalance: x => 600000 * 2,
    },
    ib: {
      imbalance: x => 1500000,
    },
    link: {
      imbalance: x => 600000,
    },
    '2pool': {
      imbalance: x => 600000,
    },
    fusdt: {
      imbalance: x => 600000 * 2,
    },
    tusd: {
      imbalance: x => 600000 * 2,
    },
    frax: {
      imbalance: x => 600000 * 2,
    },
    lusd: {
      imbalance: x => 600000 * 2,
    },
    factory: {
      imbalance: x => 600000 * 2,
    },
    alusd: {
      imbalance: x => 600000 * 2,
    },
    rai: {
      imbalance: x => 600000 * 2,
    },
    eureusd: {
      imbalance: x => 600000 * 2,
    },
    busdv2: {
      imbalance: x => 600000 * 2,
    },
    reth: {
      imbalance: x => 600000,
    },
    tricrypto: { //doesnt exist?
      imbalance: x => 600000 * 2,
    },
    tricrypto2: {
      imbalance: x => 600000 * 2,
    },
    mim: { //doesnt exist?
      imbalance: x => 600000 * 2,
    },
  }, {
    get: function(target, name) {
      if (typeof name === 'string' && name.startsWith('factory-')) return target.factory;

      if (
        typeof name === 'string' &&
        !(name in target) &&
        name !== 'inspect' &&
        name !== 'state' &&
        name !== 'render' &&
        !name.startsWith('_') // Ignore "private" props
      ) {
        throw new Error(`Property "${name}" not found on object "gas.withdraw", the pool is probably missing a config object there`);
      }

      return target[name];
    },
  }),
  depositzap: new Proxy({
    compound: {
      deposit: x => (172664 * x + 471691) * 2.5 | 0,
      withdraw: 2000000 / 1.5,
      withdrawShare: 1000000,
      withdrawImbalance: x => (181733 * x + 506125) * 1.5 | 0,
    },
    usdt: {
      //use periodic fit here?
      deposit: x => (93795.5 * x + 608935) * 2.5 | 0,
      withdraw: 2000000 / 1,
      withdrawShare: 1000000,
      withdrawImbalance: x => (97226.5 * x + 671880) * 2.5 | 0,
    },
    iearn: {
      deposit: x => (225377 * x + 522674) * 2 | 0,
      withdraw: 3500000 / 1.4,
      withdrawShare: 3500000,
      withdrawImbalance: x => (276069 * x + 516861) * 2.5 | 0,
    },
    busd: {
      deposit: x => (225377 * x + 522674) * 2 | 0,
      withdraw: 3500000 / 1.4,
      withdrawShare: 3500000,
      withdrawImbalance: x => (276069 * x + 516861) * 2.5 | 0,
    },
    susd: {
      withdrawShare: 3000000,
    },
    susdv2: {
      deposit: x => (172664 * x + 471691) * 1.5 | 0,
      withdraw: 800000,
      withdrawShare: 1000000,
      withdrawImbalance: x => (181733 * x + 506125) * 2.5 | 0,
    },
    pax: {
      deposit: x => (225377 * x + 522674) * 2 | 0,
      withdraw: 3500000 / 1.4,
      withdrawShare: 3500000,
      withdrawImbalance: x => (276069 * x + 516861) * 2.5 | 0,
    },
    //no deposit zap
    ren: {
      deposit: x => 300000,
      withdraw: 250000,
      withdrawShare: 250000,
      withdrawImbalance: x => 600000,
    },
    //no deposit zap
    sbtc: {
      deposit: x => 600000,
      withdraw: 350000,
      withdrawShare: 350000,
      withdrawImbalance: x => 800000,
    },
    //no deposit zap
    sbtc2: {
      deposit: x => 600000,
      withdraw: 350000,
      withdrawShare: 350000,
      withdrawImbalance: x => 800000,
    },
    //no deposit zap
    hbtc: {
      deposit: x => 300000,
      withdraw: 250000,
      withdrawShare: 250000,
      withdrawImbalance: x => 600000,
    },
    //no deposit zap
    '3pool': {
      deposit: x => 300000,
      withdraw: 250000,
      withdrawShare: 250000,
      withdrawImbalance: x => 600000,
    },
    '4pool': {
      deposit: x => 300000,
      withdraw: 250000,
      withdrawShare: 250000,
      withdrawImbalance: x => 600000,
    },
    'fraxusdc': {
      deposit: x => 300000,
      withdraw: 250000,
      withdrawShare: 250000,
      withdrawImbalance: x => 600000,
    },
    '2pool': {
      deposit: x => 300000,
      withdraw: 250000,
      withdrawShare: 250000,
      withdrawImbalance: x => 600000,
    },
    gusd: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    husd: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    usdk: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    usdn: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    linkusd: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    musd: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    rsv: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    tbtc: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    dusd: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    pbtc: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    bbtc: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    obtc: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    ust: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    eurs: {
      deposit: x => 600000,
      withdraw: 350000,
      withdrawShare: 350000,
      withdrawImbalance: x => 800000,
    },
    eurt: {
      deposit: x => 600000,
      withdraw: 350000,
      withdrawShare: 350000,
      withdrawImbalance: x => 800000,
    },
    seth: {
      deposit: x => 600000,
      withdraw: 250000,
      withdrawShare: 250000,
      withdrawImbalance: x => 600000,
    },
    aave: {
      deposit: x => (225377 * x + 522674) * 2 | 0,
      withdraw: 3500000 / 1.4,
      withdrawShare: 3500000,
      withdrawImbalance: x => (276069 * x + 516861) * 2.5 | 0,
    },
    geist: {
      deposit: x => (225377 * x + 522674) * 2 | 0,
      withdraw: 3500000 / 1.4,
      withdrawShare: 3500000,
      withdrawImbalance: x => (276069 * x + 516861) * 2.5 | 0,
    },
    idle: {
      deposit: x => (225377 * x + 522674) * 2 | 0,
      withdraw: 3500000 / 1.4,
      withdrawShare: 3500000,
      withdrawImbalance: x => (276069 * x + 516861) * 2.5 | 0,
    },
    steth: {
      deposit: x => 600000,
      withdraw: 250000,
      withdrawShare: 250000,
      withdrawImbalance: x => 600000,
    },
    saave: {
      deposit: x => (225377 * x + 522674) * 2 | 0,
      withdraw: 3500000 / 1.4,
      withdrawShare: 3500000,
      withdrawImbalance: x => (276069 * x + 516861) * 2.5 | 0,
    },
    ankreth: {
      deposit: x => 600000,
      withdraw: 250000,
      withdrawShare: 250000,
      withdrawImbalance: x => 600000,
    },
    usdp: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    ib: {
      deposit: x => (225377 * x + 522674) * 2 | 0,
      withdraw: 3500000 / 1.4,
      withdrawShare: 3500000,
      withdrawImbalance: x => (276069 * x + 516861) * 2.5 | 0,
    },
    link: {
      deposit: x => 300000,
      withdraw: 250000,
      withdrawShare: 250000,
      withdrawImbalance: x => 600000,
    },
    '2pool': {
      deposit: x => 300000,
      withdraw: 250000,
      withdrawShare: 250000,
      withdrawImbalance: x => 600000,
    },
    fusdt: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    tusd: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    frax: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    lusd: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    factory: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    busdv2: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    reth: {
      deposit: x => 600000,
      withdraw: 250000,
      withdrawShare: 250000,
      withdrawImbalance: x => 600000,
    },
    alusd: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    rai: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    eureusd: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    tricrypto: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    tricrypto2: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    atricrypto: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    atricrypto2: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    atricrypto3: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    aaveV3: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    mim: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    eurtusd: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    euroc: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    frxeth: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    xautusd: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    eursusd: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    crveth: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    cvxeth: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    teth: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
    spelleth: {
      deposit: x => 300000 * 4,
      withdraw: 250000 * 4,
      withdrawShare: 250000 * 4,
      withdrawImbalance: x => 600000 * 4,
    },
  }, {
    get: function(target, name) {
      if (typeof name === 'string' && name.startsWith('factory-')) return target.factory;

      if (
        typeof name === 'string' &&
        !(name in target) &&
        name !== 'inspect' &&
        name !== 'state' &&
        name !== 'render' &&
        !name.startsWith('_') // Ignore "private" props
      ) {
        throw new Error(`Property "${name}" not found on object "gas.depositzap", the pool is probably missing a config object there`);
      }

      return target[name];
    },
  }),
  adapter: {
    ren: {
      mintThenSwap: 600001,
      mintThenDeposit: 1100001,
      swapThenBurn: 600001,
      removeLiquidityThenBurn: 400001,
      removeLiquidityImbalanceThenBurn: 600001,
      removeLiquidityOneCoinThenBurn: 400001,
    },
    sbtc: {
      mintThenSwap: 1100001,
      mintThenDeposit: 1100001,
      swapThenBurn: 1100001,
      removeLiquidityThenBurn: 1100001,
      removeLiquidityImbalanceThenBurn: 1100001,
      removeLiquidityOneCoinThenBurn: 1100001,
    },
  }
};

let initState = (pool) => ({
  deposit_zap: null,
  balances: [],
  wallet_balances: [],
  underlying_coins: [],
  all_coins: [],
  c_rates: [],
  bal_info: [],
  total: 0,
  l_info: [],
  totalShare: 0,
  showShares: false,
  totalSupply: 0,
  totalBalance: 0,
  usdShare: null,
  swapbtc: false,
  gaugeContract: null,
  minter: null,
  wrapped_precisions: (pool && contractAbis[pool]) ? contractAbis[pool].wrapped_precisions : undefined,
  priceScale: [],
  priceOracle: [],
});

const state = Vue.observable({
  web3: null,
  multicall: null,
  walletName: null,
  allInitContracts: new Set(),
  contracts: new Proxy((config.blockchainId === 'ethereum' ? {
    compound: {
      currentContract: 'compound',
      ...initState('compound'),
    },
    usdt: {
      currentContract: 'usdt',
      ...initState('usdt'),
    },
    iearn: {
      currentContract: 'iearn',
      aRewards: null,
      ...initState('iearn'),
    },
    busd: {
      currentContract: 'busd',
      ...initState('busd'),
    },
    susdv2: {
      currentContract: 'susdv2',
      ...initState('susdv2'),
      initial_A: null,
      initial_A_time: null,
      future_A_time: null,
      usdStake: null,
      curveRewards: null,
      snxExchanger: null,
    },
    pax: {
      currentContract: 'pax',
      ...initState('pax'),
    },
    ren: {
      currentContract: 'ren',
      adapterContract: null,
      ...initState('ren'),
    },
    sbtc: {
      currentContract: 'sbtc',
      snxExchanger: null,

      ...initState('sbtc'),
    },
    sbtc2: {
      currentContract: 'sbtc2',
      snxExchanger: null,

      ...initState('sbtc2'),
    },
    hbtc: {
      currentContract: 'hbtc',

      ...initState('hbtc'),
    },
    '3pool': {
      currentContract: '3pool',

      ...initState('3pool'),
    },
    '2pool': {
      currentContract: '2pool',

      ...initState('2pool'),
    },
    '4pool': {
      currentContract: '4pool',

      ...initState('4pool'),
    },
    'fraxusdc': {
      currentContract: 'fraxusdc',

      ...initState('fraxusdc'),
    },
    gusd: {
      currentContract: 'gusd',
      is_meta: true,
      base_swap: null,
      base_token: null,
      base_supply: null,
      ...initState('gusd'),
    },
    husd: {
      currentContract: 'husd',
      is_meta: true,
      base_swap: null,
      base_token: null,
      base_supply: null,
      ...initState('husd'),
    },
    usdk: {
      currentContract: 'usdk',
      is_meta: true,
      base_swap: null,
      base_token: null,
      base_supply: null,
      ...initState('usdk'),
    },
    usdn: {
      currentContract: 'usdn',
      is_meta: true,
      base_swap: null,
      base_token: null,
      base_supply: null,
      ...initState('usdn'),
    },
    linkusd: {
      currentContract: 'linkusd',
      is_meta: true,
      base_swap: null,
      base_token: null,
      base_supply: null,
      ...initState('linkusd'),
    },
    musd: {
      currentContract: 'musd',
      is_meta: true,
      base_swap: null,
      base_token: null,
      base_supply: null,
      ...initState('musd'),
    },
    rsv: {
      currentContract: 'rsv',
      is_meta: true,
      base_swap: null,
      base_token: null,
      base_supply: null,
      ...initState('rsv'),
    },
    tbtc: {
      currentContract: 'tbtc',
      snxExchanger: null,

      is_meta: true,
      base_swap: null,
      base_token: null,
      base_supply: null,
      ...initState('tbtc'),
    },
    dusd: {
      currentContract: 'dusd',
      is_meta: true,
      base_swap: null,
      base_token: null,
      base_supply: null,
      ...initState('dusd'),
    },
    pbtc: {
      currentContract: 'pbtc',
      snxExchanger: null,

      is_meta: true,
      base_swap: null,
      base_token: null,
      base_supply: null,
      ...initState('pbtc'),
    },
    bbtc: {
      currentContract: 'bbtc',
      snxExchanger: null,

      is_meta: true,
      base_swap: null,
      base_token: null,
      base_supply: null,
      ...initState('bbtc'),
    },
    obtc: {
      currentContract: 'obtc',
      snxExchanger: null,

      is_meta: true,
      base_swap: null,
      base_token: null,
      base_supply: null,
      ...initState('obtc'),
    },
    ust: {
      currentContract: 'ust',
      is_meta: true,
      base_swap: null,
      base_token: null,
      base_supply: null,
      ...initState('ust'),
    },
    eurs: {
      currentContract: 'eurs',
      ...initState('eurs'),
    },
    eurt: {
      currentContract: 'eurt',
      ...initState('eurt'),
    },
    seth: {
      currentContract: 'seth',
      ...initState('seth'),
    },
    aave: {
      currentContract: 'aave',
      ...initState('aave'),
    },
    steth: {
      currentContract: 'steth',
      ...initState('steth'),
    },
    saave: {
      currentContract: 'saave',
      ...initState('saave'),
    },
    ankreth: {
      currentContract: 'ankreth',
      ...initState('ankreth'),
    },
    usdp: {
      currentContract: 'usdp',
      is_meta: true,
      base_swap: null,
      base_token: null,
      base_supply: null,
      ...initState('usdp'),
    },
    ib: {
      currentContract: 'ib',
      ...initState('ib'),
    },
    link: {
      currentContract: 'link',
      ...initState('link'),
    },
    tusd: {
      currentContract: 'tusd',
      is_meta: true,
      base_swap: null,
      base_token: null,
      base_supply: null,
      ...initState('tusd'),
    },
    frax: {
      currentContract: 'frax',
      is_meta: true,
      base_swap: null,
      base_token: null,
      base_supply: null,
      ...initState('frax'),
    },
    lusd: {
      currentContract: 'lusd',
      is_meta: true,
      base_swap: null,
      base_token: null,
      base_supply: null,
      ...initState('lusd'),
    },
    alusd: {
      currentContract: 'alusd',
      is_meta: true,
      base_swap: null,
      base_token: null,
      base_supply: null,
      ...initState('alusd'),
    },
    rai: {
      currentContract: 'rai',
      is_meta: true,
      base_swap: null,
      base_token: null,
      base_supply: null,
      ...initState('rai'),
    },
    eureusd: {
      currentContract: 'eureusd',
      is_meta: true,
      base_swap: null,
      base_token: null,
      base_supply: null,
      ...initState('eureusd'),
    },
    busdv2: {
      currentContract: 'busdv2',
      is_meta: true,
      base_swap: null,
      base_token: null,
      base_supply: null,
      ...initState('busdv2'),
    },
    reth: {
      currentContract: 'reth',
      ...initState('reth'),
    },
    tricrypto: {
      currentContract: 'tricrypto',
      ...initState('tricrypto'),
    },
    tricrypto2: {
      currentContract: 'tricrypto2',
      ...initState('tricrypto2'),
    },
    mim: {
      currentContract: 'mim',
      ...initState('mim'),
    },
    eurtusd: {
      currentContract: 'eurtusd',
      ...initState('eurtusd'),
    },
    euroc: {
      currentContract: 'euroc',
      ...initState('euroc'),
    },
    frxeth: {
      currentContract: 'frxeth',
      ...initState('frxeth'),
    },
    xautusd: {
      currentContract: 'xautusd',
      ...initState('xautusd'),
    },
    eursusd: {
      currentContract: 'eursusd',
      ...initState('eursusd'),
    },
    crveth: {
      currentContract: 'crveth',
      ...initState('crveth'),
    },
    cvxeth: {
      currentContract: 'cvxeth',
      ...initState('cvxeth'),
    },
    teth: {
      currentContract: 'teth',
      ...initState('teth'),
    },
    spelleth: {
      currentContract: 'spelleth',
      ...initState('spelleth'),
    },
  } : {
    '2pool': {
      currentContract: '2pool',
      ...initState('2pool'),
    },
    'ib': {
      currentContract: 'ib',
      ...initState('ib'),
    },
    'geist': {
      currentContract: 'geist',
      ...initState('geist'),
    },
    '3pool': {
      currentContract: '3pool',
      ...initState('3pool'),
    },
    '2pool': {
      currentContract: '2pool',
      ...initState('2pool'),
    },
    '4pool': {
      currentContract: '4pool',
      ...initState('4pool'),
    },
    'fraxusdc': {
      currentContract: 'fraxusdc',
      ...initState('fraxusdc'),
    },
    fusdt: {
      currentContract: 'fusdt',
      ...initState('fusdt'),
    },
    aave: {
      currentContract: 'aave',
      ...initState('aave'),
    },
    aaveV3: {
      currentContract: 'aaveV3',
      ...initState('aaveV3'),
    },
    tricrypto: {
      currentContract: 'tricrypto',
      ...initState('tricrypto'),
    },
    tricrypto2: {
      currentContract: 'tricrypto2',
      ...initState('tricrypto2'),
    },
    atricrypto: {
      currentContract: 'atricrypto',
      ...initState('atricrypto'),
    },
    atricrypto2: {
      currentContract: 'atricrypto2',
      ...initState('atricrypto2'),
    },
    atricrypto3: {
      currentContract: 'atricrypto3',
      ...initState('atricrypto3'),
    },
    aaveV3: {
      currentContract: 'aaveV3',
      ...initState('aaveV3'),
    },
    ren: {
      currentContract: 'ren',
      adapterContract: null,
      ...initState('ren'),
    },
    eurtusd: {
      currentContract: 'eurtusd',
      ...initState('eurtusd'),
    },
    euroc: {
      currentContract: 'euroc',
      ...initState('euroc'),
    },
    frxeth: {
      currentContract: 'frxeth',
      ...initState('frxeth'),
    },
    xautusd: {
      currentContract: 'xautusd',
      ...initState('xautusd'),
    },
    eursusd: {
      currentContract: 'eursusd',
      ...initState('eursusd'),
    },
    crveth: {
      currentContract: 'crveth',
      ...initState('crveth'),
    },
    cvxeth: {
      currentContract: 'cvxeth',
      ...initState('cvxeth'),
    },
    teth: {
      currentContract: 'teth',
      ...initState('teth'),
    },
    spelleth: {
      currentContract: 'spelleth',
      ...initState('spelleth'),
    },
    rai: {
      currentContract: 'rai',
      is_meta: true,
      base_swap: null,
      base_token: null,
      base_supply: null,
      ...initState('rai'),
    },
    eureusd: {
      currentContract: 'eureusd',
      is_meta: true,
      base_swap: null,
      base_token: null,
      base_supply: null,
      ...initState('eureusd'),
    },
  }), {
    get: function(target, name) {
      if (typeof name === 'string' && name.startsWith('factory-')) {
        return {
          currentContract: name,
          is_meta: true,
          base_swap: null,
          base_token: null,
          base_supply: null,
          ...initState(name),
        };
      }

      if (
        typeof name === 'string' &&
        !(name in target) &&
        name !== 'inspect' &&
        name !== 'state' &&
        name !== 'render' &&
        !name.startsWith('_') // Ignore "private" props
      ) {
        throw new Error(`Property "${name}" not found on object "state.contracts", the pool is probably missing a config object there`);
      }

      return target[name];
    },
  }),
  is_meta: false,
  base_swap: null,
  base_token: null,
  base_supply: null,
  swapbtc: false,
  adapterContract: null,
  currentContract: pools.length > 0 ? pools[0].id : null,
  currencies: pools.length > 0 ? currencies[pools[0].id] : null,
  lending_currencies: pools.length > 0 ? lending_currencies[pools[0].id] : null,
  N_COINS: N_COINS,
  coin_precisions: coin_precisions,
  wrapped_precisions: pools.length > 0 ? contractAbis[pools[0].id].wrapped_precisions : null,
  swap_address: swap_address,
  old_swap_address: old_swap_address,
  token_address: token_address,
  old_token_address: old_token_address,
  migration_address: migration_address,
  infura_url: infura_url,
  has_eth: null,
  is_aave: false,
  is_factory: false,
  is_new_underlying: false,
  is_idle: false,
  swap: null,
  old_swap: null,
  swap_token: null,
  old_swap_token: null,

  totalBalance: null,
  totalSupply: null,
  oldBalance: null,
  provider: null,
  signer: null,
  ERC20Contract: null,
  balances: new Array(N_COINS),
  wallet_balances: new Array(N_COINS),
  fee: 0,
  admin_fee: null,
  trade_timeout: 1800,
  max_allowance: BN(2).pow(BN(256)).minus(BN(1)),
  coins: [],
  underlying_coins: [],
  c_rates: [],
  bal_info: [],
  total: 0,
  l_info: [],
  totalShare: 0,
  showShares: false,

  staked_info: [],
  priceScale: [],
  priceOracle: [],
  initial_A_gamma: null,
  future_A_gamma: null,
  initial_A_gamma_time: null,
  future_A_gamma_time: null,

  totalStake: -1,

  virtual_price: null,
  A: null,
  initial_A: null,
  initial_A_time: null,
  future_A: null,
  future_A_time: null,
  admin_actions_deadline: null,

  slippage: 0,
  showSlippage: false,
  showNoBalance: false,
  showNoBalanceCoin: '',

  initializedContracts: false,

  default_account: '',

  error: false,
  networkIdError: false,
  networkDelayError: null,
  curveRewards: null,
  curveStakedBalance: null,

  usdShare: null,
  usdStake: null,

  curveRewards: null,
  snxExchanger: null,

  chi: null,

  aRewards: null,

})

export let contract = state

export const getters = {
  default_account: () => state.default_account || '0x0000000000000000000000000000000000000000',
  walletName: () => state.walletName,
  walletProvider: () => state.walletProvider,
  ethers: () => state.ethers,
  currentPool: () => state.currentContract,
  currentPoolRouteId: () => pools.getById(state.currentContract).routeOverride || state.currentContract,
  oldBalance: () => state.oldBalance,
  bal_info: () => state.bal_info,
  balTotal: () => state.total,
  l_info: () => state.l_info,
  totalShare: () => state.totalShare,
  totalBalance: () => state.totalBalance / 1e18,
  totalSupply: () => state.totalSupply / 1e18,
  currencies: () => state.currencies,
  is_meta: () => state.is_meta,
  has_eth: () => state.has_eth,
  is_aave: () => state.is_aave,
  is_factory: () => state.is_factory,
  is_new_underlying: () => state.is_new_underlying,
  is_idle: () => state.is_idle,
  lending_currencies: () => state.lending_currencies,
  meta_currencies: () => state.meta_currencies,
  all_currencies: () => state.all_currencies,
  hasGauge: (pool) => {
    const poolId = (typeof pool === 'string' && pool) || state.currentContract;
    const normalizedPoolId = poolId === 'susd' ? 'susdv2' : poolId;
    return !pools.getById(normalizedPoolId).hasNoGauge;
  },
  fee: () => state.fee * 100,
  admin_fee: () => state.admin_fee !== null ? state.admin_fee * 100 : null,
  A: () => state.A,
  initial_A: () => state.initial_A,
  initial_A_time: () => state.initial_A_time,
  future_A: () => state.future_A,
  future_A_time: () => state.future_A_time,
  admin_actions_deadline: () => state.admin_actions_deadline,
  initializedContracts: () => state.initializedContracts,
  showSlippage: () => state.showSlippage,
  slippage: () => state.slippage,
  N_COINS: () => state.N_COINS,
  error: () => state.error,
  networkIdError: () => state.networkIdError,
  networkDelayError: () => state.networkDelayError,
  showShares: () => state.showShares,

  staked_info: () => state.staked_info,
  totalStake: () => state.totalStake,

  usdShare: () => state.usdShare,
  usdStake: () => state.usdStake,

  base_supply: () => state.base_supply,
}

const handleContractAbisUpdate = (contract) => {
  const pool = pools.getById(contract.currentContract);
  const gaugeABI = pool.gaugeVersion === 4 ? liquiditygauge_v4 : pool.gaugeVersion === 3 ? liquiditygauge_v3 : pool.gaugeVersion === 2 ? liquiditygauge_abi_v2 : liquiditygauge_abi;

  contract.gaugeContract = new state.web3.eth.Contract(gaugeABI, contractAbis[contract.currentContract].gauge_address);

  if (contractAbis[contract.currentContract].old_gauge) {
    const oldGaugeVersion = pool.oldAdditionalRewardsGaugeVersion || pool.gaugeVersion
    const oldGaugeABI = oldGaugeVersion === 4 ? liquiditygauge_v4 : oldGaugeVersion === 3 ? liquiditygauge_v3 : oldGaugeVersion === 2 ? liquiditygauge_abi_v2 : liquiditygauge_abi;

    contract.oldGauge = new state.web3.eth.Contract(oldGaugeABI, contractAbis[contract.currentContract].old_gauge);
  }

  if (contractAbis[contract.currentContract].old_gauge_2) {
    const oldGauge2Version = pool.oldAdditionalRewardsGaugeVersion || pool.gaugeVersion
    const oldGauge2ABI = oldGauge2Version === 4 ? liquiditygauge_v4 : oldGauge2Version === 3 ? liquiditygauge_v3 : oldGauge2Version === 2 ? liquiditygauge_abi_v2 : liquiditygauge_abi;

    contract.oldGauge2 = new state.web3.eth.Contract(oldGauge2ABI, contractAbis[contract.currentContract].old_gauge_2);
  }
};

let isInitialized = false;
export async function init(contract, refresh = false) {
  isInitialized = false;
  state.multicall = state.multicall || new state.web3.eth.Contract(multicall_abi, multicall_address)
  console.time('init')
  if (state.initializedContracts && contract?.currentContract == state.currentContract && !refresh) return Promise.resolve();
  if (contract && (contract.currentContract == state.currentContract || state.contracts[contract.currentContract].initializedContracts) && !refresh) return Promise.resolve();


  if (!contract) contract = state


  try {
    let networkId = await state.web3.eth.net.getId();
    const networkIdError = (
      (config.blockchainId !== 'avalanche' && networkId !== config.networkId) ||
      (config.blockchainId === 'avalanche' && networkId !== config.networkId && networkId !== 1)
    );
    if (networkIdError) {
      state.error = 'Error: wrong network type. Please switch to mainnet';
    }
  }
  catch (err) {
    console.error(err);
    state.error = 'There was an error connecting. Please refresh page';
  }

  contract.minter = new state.web3.eth.Contract(minter_ABI, minter_address)

  handleContractAbisUpdate(contract);

  if (pools.getById(contract.currentContract).addresses.migrator) {
    contract.migrator = new state.web3.eth.Contract(migratorABI, pools.getById(contract.currentContract).addresses.migrator);
  }

  if (['ren', 'sbtc'].includes(contract.currentContract))
    state.chi = state.chi || new state.web3.eth.Contract(ERC20_abi, CHI_address)

  let calls;
  if (contractAbis[contract.currentContract].is_factory && contractAbis[contract.currentContract].pool_type !== 'crypto') {
    calls = [
      //get_virtual_price
      [contractAbis[contract.currentContract].swap_address, '0xbb7b8b80'],
      //A
      [contractAbis[contract.currentContract].swap_address, '0xf446c1d0'],
      //future_A
      [contractAbis[contract.currentContract].swap_address, '0xb4b577ad'],
      //admin_actions_deadline
      [contractAbis[contract.currentContract].swap_address, '0xb4b577ad'],
    ];
  } else if (contractAbis[contract.currentContract].pool_type == 'crypto') {
    calls = [
      //get_virtual_price
      [contractAbis[contract.currentContract].swap_address, '0xbb7b8b80'],
      //A
      [contractAbis[contract.currentContract].swap_address, '0xf446c1d0'],
      //future_A
      [contractAbis[contract.currentContract].swap_address, '0xf446c1d0'],
      //admin_actions_deadline
      [contractAbis[contract.currentContract].swap_address, '0xf446c1d0'],
    ];
  } else {
    calls = [
      //get_virtual_price
      [contractAbis[contract.currentContract].swap_address, '0xbb7b8b80'],
      //A
      [contractAbis[contract.currentContract].swap_address, '0xf446c1d0'],
      //future_A
      [contractAbis[contract.currentContract].swap_address, '0xb4b577ad'],
      //admin_actions_deadline
      [contractAbis[contract.currentContract].swap_address, '0x405e28f8'],
    ];
  }


  if (contractAbis[contract.currentContract].is_meta) {
    contract.is_meta = true
    contract.base_swap = new state.web3.eth.Contract(contractAbis[contractAbis[contract.currentContract].base_pool].swap_abi, contractAbis[contractAbis[contract.currentContract].base_pool].swap_address)
    contract.base_token = new state.web3.eth.Contract(ERC20_abi, contractAbis[contractAbis[contract.currentContract].base_pool].token_address)
  } else {
    contract.is_meta = false
  }



  if (contractAbis[contract.currentContract].is_factory) {
    contract.is_factory = true
  } else {
    contract.is_factory = false
  }

  if (contractAbis[contract.currentContract].has_eth) {
    contract.has_eth = true
  } else {
    contract.has_eth = false
  }
  if (contractAbis[contract.currentContract].is_aave) {
    contract.is_aave = true
  } else {
    contract.is_aave = false
  }
  if (contractAbis[contract.currentContract].is_new_underlying) {
    contract.is_new_underlying = true
  } else {
    contract.is_new_underlying = false
  }


  if (contractAbis[contract.currentContract].is_idle) {
    contract.is_idle = true
  } else {
    contract.is_idle = false
  }

  if (contract.currentContract == 'compound') {
    state.old_swap = new state.web3.eth.Contract(contractAbis.compound.old_swap_abi, old_swap_address);
    state.old_swap_token = new state.web3.eth.Contract(ERC20_abi, old_token_address);
    calls.push([state.old_swap_token._address, state.old_swap_token.methods.balanceOf(state.default_account || '0x0000000000000000000000000000000000000000').encodeABI()])
  }
  if (['ren', 'sbtc'].includes(contract.currentContract) && config.blockchainId === 'ethereum') {
    state.adapterContract = new state.web3.eth.Contract(contractAbis[contract.currentContract].adapterABI, contractAbis[contract.currentContract].adapterBiconomyAddress)
  }
  if (contract.currentContract == 'susdv2') {
    //balanceOf(address)
    let default_account = state.default_account || '0x0000000000000000000000000000000000000000'
    calls.push([susd.token_address, '0x70a08231000000000000000000000000' + default_account.slice(2)])

    contract.curveRewards = new state.web3.eth.Contract(contractAbis.susdv2.sCurveRewards_abi, contractAbis.susdv2.sCurveRewards_address)
    //calls.push([contract.curveRewards._address, contract.curveRewards.methods.balanceOf(state.default_account || '0x0000000000000000000000000000000000000000').encodeABI()])

    contract.snxExchanger = new state.web3.eth.Contract(synthetixExchanger_ABI, synthetixExchanger_address)
  }
  if (contract.currentContract == 'sbtc') {

    contract.curveRewards = new state.web3.eth.Contract(contractAbis.sbtc.sCurveRewards_abi, contractAbis.sbtc.sCurveRewards_address)
    //calls.push([contract.curveRewards._address, contract.curveRewards.methods.balanceOf(state.default_account || '0x0000000000000000000000000000000000000000').encodeABI()])

    contract.snxExchanger = new state.web3.eth.Contract(synthetixExchanger_ABI, synthetixExchanger_address)
  }
  if (contract.currentContract == 'tbtc') {

    contract.curveRewards = new state.web3.eth.Contract(contractAbis.tbtc.sCurveRewards_abi, contractAbis.tbtc.sCurveRewards_address)

    contract.snxExchanger = new state.web3.eth.Contract(synthetixExchanger_ABI, synthetixExchanger_address)
  }
  if (['iearn', 'y'].includes(contract.currentContract)) {
    contract.aRewards = new state.web3.eth.Contract(contractAbis.iearn.aRewards_abi, contractAbis.iearn.aRewards_address)
    contract.curveRewards = new state.web3.eth.Contract(contractAbis.iearn.sCurveRewards_abi, contractAbis.iearn.sCurveRewards_address)
    //calls.push([contract.curveRewards._address, contract.curveRewards.methods.balanceOf(state.default_account || '0x0000000000000000000000000000000000000000').encodeABI()])
  }
  if (contract.currentContract == 'musd') {
    contract.curveRewards = new state.web3.eth.Contract(contractAbis.musd.sCurveRewards_abi, contractAbis.musd.sCurveRewards_address)
  }
  if (contract.currentContract == 'rsv') {
    contract.curveRewards = new state.web3.eth.Contract(contractAbis.musd.sCurveRewards_abi, contractAbis.musd.sCurveRewards_address)
  }
  if (contract.currentContract == 'dusd') {
    contract.curveRewards = new state.web3.eth.Contract(contractAbis.musd.sCurveRewards_abi, contractAbis.musd.sCurveRewards_address)
  }
  if (contract.currentContract == 'pbtc') {
    contract.snxExchanger = new state.web3.eth.Contract(synthetixExchanger_ABI, synthetixExchanger_address)
    contract.curveRewards = new state.web3.eth.Contract(contractAbis.pbtc.sCurveRewards_abi, contractAbis.pbtc.sCurveRewards_address)

  }

  if (contract.currentContract == 'bbtc') {
    contract.snxExchanger = new state.web3.eth.Contract(synthetixExchanger_ABI, synthetixExchanger_address)
  }

  if (contract.currentContract == 'obtc') {
    contract.snxExchanger = new state.web3.eth.Contract(synthetixExchanger_ABI, synthetixExchanger_address)
    contract.curveRewards = new state.web3.eth.Contract(contractAbis.obtc.sCurveRewards_abi, contractAbis.obtc.sCurveRewards_address)
  }

  if (contract.currentContract == 'eurt' || contract.currentContract == 'eurs') {
    contract.snxExchanger = new state.web3.eth.Contract(synthetixExchanger_ABI, synthetixExchanger_address)

  }

  if (contract.currentContract == 'seth') {
    contract.snxExchanger = new state.web3.eth.Contract(synthetixExchanger_ABI, synthetixExchanger_address)
  }

  if (contract.currentContract == 'steth') {
    contract.snxExchanger = new state.web3.eth.Contract(synthetixExchanger_ABI, synthetixExchanger_address)
    contract.curveRewards = new state.web3.eth.Contract(contractAbis.steth.sCurveRewards_abi, contractAbis.steth.sCurveRewards_address)
  }

  if (contract.currentContract == 'saave') {
    contract.snxExchanger = new state.web3.eth.Contract(synthetixExchanger_ABI, synthetixExchanger_address)
  }

  if (contract.currentContract == 'link') {
    contract.snxExchanger = new state.web3.eth.Contract(synthetixExchanger_ABI, synthetixExchanger_address)
  }

  if (pools.getById(contract.currentContract).isFakePlainPool) {
    //init the zap
    contract.crypto_zap = new state.web3.eth.Contract(contractAbis[contract.currentContract].deposit_abi, pools.getById(contract.currentContract).addresses.deposit)
    contract.base_swap = new state.web3.eth.Contract(contractAbis[contractAbis[contract.currentContract].base_pool].swap_abi, contractAbis[contractAbis[contract.currentContract].base_pool].swap_address)
    contract.base_token = new state.web3.eth.Contract(ERC20_abi, contractAbis[contractAbis[contract.currentContract].base_pool].token_address)
  }

  calls.push([contract.gaugeContract._address, contract.gaugeContract.methods.balanceOf(state.default_account || '0x0000000000000000000000000000000000000000').encodeABI()])

  if (!pools.getById(contract.currentContract).isOldPool && !pools.getById(contract.currentContract).cryptoPool) {
    //initial_A
    calls.push([contractAbis[contract.currentContract].swap_address, '0x5409491a'])
    //initial_A_time
    calls.push([contractAbis[contract.currentContract].swap_address, '0x2081066c'])
    //future_A_time
    calls.push([contractAbis[contract.currentContract].swap_address, '0x14052288'])
  } else if (pools.getById(contract.currentContract).cryptoPool) {

    calls.push([contractAbis[contract.currentContract].swap_address, '0xbb7b8b80'])
    calls.push([contractAbis[contract.currentContract].swap_address, '0xbb7b8b80'])
    calls.push([contractAbis[contract.currentContract].swap_address, '0xbb7b8b80'])

  }


  if (contract.is_meta) {
    calls.push([contract.base_token._address, contract.base_token.methods.totalSupply().encodeABI()])
  }




  if (config.blockchainId === 'ethereum') {
    if (!['susd', 'ren', 'sbtc', 'hbtc', '3pool', 'eurs', 'seth', 'aave', 'idle', 'steth', 'saave', 'ankreth', 'ib', 'link', 'reth', 'eurt', '2pool', '4pool', 'fraxusdc'].includes(contract.currentContract) && contractAbis[state.currentContract].deposit_abi) {
      state.deposit_zap = new state.web3.eth.Contract(contractAbis[state.currentContract].deposit_abi, contractAbis[state.currentContract].deposit_address);
    }
  }

  if (contractAbis[contract.currentContract].deposit_address) {
    //this should remove the need for the conditional above but I'm not sure it won't break anything.
    try {
      state.deposit_zap = new state.web3.eth.Contract(contractAbis[contract.currentContract].deposit_abi, contractAbis[contract.currentContract].deposit_address);
    } catch (e) {
      console.log('No deposit_zap')
    }
  }


  contract.swap = new state.web3.eth.Contract(contractAbis[contract.currentContract].swap_abi, contractAbis[contract.currentContract].swap_address);
  contract.swap_token = new state.web3.eth.Contract(ERC20_abi, contractAbis[contract.currentContract].token_address);

  if (contract.is_meta) {
    contract.deposit_zap = new state.web3.eth.Contract(contractAbis[contract.currentContract].deposit_abi, contractAbis[contract.currentContract].deposit_address);
  }

  contract.coins = []
  contract.underlying_coins = []
  if (window.location.href.includes('withdraw_old'))
    calls.push(...(await common.update_fee_info('old', contract, false)))
  else
    calls.push(...(await common.update_fee_info('new', contract, false)));
  // for (let i = 0; i < contractAbis[contract.currentContract].N_COINS; i++) {
  // let coinsCall = contract.swap.methods.coins(i).encodeABI()
  // let underlyingCoinsCall = ['tbtc', 'ren', 'sbtc', 'hbtc', '3pool'].includes(contract.currentContract) ?
  // 							contract.swap.methods.coins(i).encodeABI()
  // 							: contract.swap.methods.underlying_coins(i).encodeABI();
  // 	calls.push([contract.swap._address, coinsCall])
  // 	calls.push([contract.swap._address, underlyingCoinsCall])
  // }

  //console.log(calls, 'calls');
  await common.multiInitState(calls, contract, true)
  contract.initializedContracts = true;
  console.timeEnd('init')
  state.allInitContracts = new Set(state.allInitContracts.add(contract.currentContract))
  //console.log([...state.allInitContracts])

  sleepUntil(() => isFactoryPoolDataLoaded() && isFactoryAbiDataLoaded()).then(() => {
    handleContractAbisUpdate(contract);
    isInitialized = true;
  });
}

export const allState = Vue.observable({
  swap: {},
  underlying_coins: {},
})

export async function getAllUnderlying() {
  for ([key, contract] of Object.entries(contractAbis)) {
    if (key == 'susd') continue;
    allState.swap[key] = new state.web3.eth.Contract(contract.swap_abi, contract.swap_address);
    allState.underlying_coins[key] = [];
    for (let i = 0; i < contract.N_COINS; i++) {
      var addr = await allState.swap[key].methods.coins(i).call();
      var underlying_addr = await allState.swap[key].swap.methods.underlying_coins(i).call();
      allState.underlying_coins[key][i] = new state.web3.eth.Contract(ERC20_abi, underlying_addr);
    }
  }
}

export async function changeContract(pool) {
  const hasPoolInConfig = !!pools.getById(pool, false);
  if (!hasPoolInConfig) await sleepUntil(() => isFactoryAbiDataLoaded());
  //re-init contract with different pool
  if (!(pool in contractAbis)) return;

  state.initializedContracts = false;
  state.currentContract = pool;
  state.currencies = currencies[pool]
  state.lending_currencies = lending_currencies[pool] || []
  state.meta_currencies = meta_currencies[pool] || []
  state.all_currencies = all_currencies[pool] || currencies[pool] || []
  state.N_COINS = contractAbis[pool].N_COINS;
  state.meta_N = contractAbis[pool].meta_N;
  //state.deposit_zap = null;
  state.coin_precisions = contractAbis[pool].coin_precisions;
  state.wrapped_precisions = contractAbis[pool].wrapped_precisions;
  state.swap_address = contractAbis[pool].swap_address;
  state.old_swap_address = contractAbis[pool].old_swap_address;
  state.token_address = contractAbis[pool].token_address;
  state.old_token_address = contractAbis[pool].old_token_address;
  state.migration_address = contractAbis[pool].migration_address;
  state.coins = new Array(contractAbis[pool].coins);
  state.underlying_coins = new Array(contractAbis[pool].underlying_coins);
  state.balances = new Array(contractAbis[pool].balances);
  state.wallet_balances = new Array(contractAbis[pool].wallet_balances);
  state.bal_info = []
  state.l_info = []
  state.total = 0
  state.totalShare = 0
  await web3Init();
}

export function setCurrencies(pool) {
  contract.currencies = currencies[pool]
}

export async function initusdt() {
  await init(state.contracts.usdt)
}

export function isContractDataLoaded() {
  return isInitialized;
}
